import React from 'react';
import { formatCurrency, generateId, translate } from '../../utility/functions';
import Table, { Ctd } from '../../table/Table';
import Button from '../../forms/Button';
import GetState from '../../GetState';
import { QuestRoute } from '../../api/QuestRoutes';
import Form from '../../forms/Form';
import HamQuest from '../../api/HamQuestGiver';
import Input from '../../forms/Input';
import Preput from '../../forms/Preput';
import Select from '../../forms/Select';
import { HSOptions } from '../../../constants/HSOptions';
import Checkbox from '../../forms/Checkbox';
import TerminalIDsSelector from '../../forms/formComponents/TerminalIDsSelector';
import LocationSelectors from '../../forms/formComponents/LocationSelectors';
import ImageIndex from '../../forms/formComponents/ImageIndex';
import ColorInput from '../../forms/formComponents/ColorInput';
import { userRoles } from '../../RoleProtected';
import ManagementRow from '../../table/ManagementRow';
import ModalFactory from '../../Modal';
import CheckMark from '../../CheckMark';
import Loading from '../../Loading';

const Products = ({ ...props }) => {
    const [kioskItems = []] = GetState(QuestRoute.kiosks.wash);
    const [products, refreshProducts] = GetState(QuestRoute.products.all);

    if (!kioskItems && !products) return <Loading />;

    const deleteProduct = (product) => {
        ModalFactory.confirmAndProcess({
            confirmation: {
                title: `Are you sure?`,
                body: `Are you sure you want to delete ${product.name}?`,
                type: 'warning',
            },
            promise: {
                title: 'Deleting Product',
                body: `Deleting ${product.name}`,
                callback: () => HamQuest.delete(QuestRoute.products.edit(product.appProductId))
            },
            confirmed: {
                title: 'Success',
                body: `${product.name} successfully deleted.`,
                type: 'success'
            },
            finally: () => refreshProducts()
        })
    }

    const MobileProducts = ({ values, ...props }) => {
        return (
            <>
                <Checkbox
                    label='Active'
                    name='active'
                />
                <Select
                    label='Product Type'
                    name='appProductType'
                    options={HSOptions.productType}
                    required
                />

                <Select
                    label='Kiosk Item'
                    name='kioskItemId'
                    options={kioskItems.map(e => ({ label: e.name, value: e.id }))}
                    required
                />
                <Checkbox
                    label='Use Kiosk Item Name'
                    name='useKioskItemName'
                />
                <Input
                    label='Name'
                    name='name'
                    disable={values.useKioskItemName}
                    required={!values.useKioskItemName}
                />
                <Checkbox
                    label='Use Kiosk Item Details'
                    name='useKioskItemDetails'
                />
                <Preput
                    label='Details'
                    name='details'
                    disabled={values.useKioskItemDetails}
                />

                <Input
                    label='Quantity'
                    name='quantity'
                    type='number'
                    disable={values.productType !== 'AppBundle'}
                    min={1}
                />
                <Checkbox
                    label='Use Kiosk Price'
                    name='useKioskItemPrice'
                    disabled={values.productType === 'AppBundle'}
                />

                <Input
                    label='Price'
                    name='price'
                    type='price'
                    disable={values.useKioskItemPrice && values.productType !== 'AppBundle'}
                    required={!values.useKioskItemPrice && values.productType !== 'AppBundle'}
                />
                <Checkbox
                    label='Is taxed'
                    name='taxable'
                />

                <Input
                    label='Limit Start Date'
                    name='purchaseLimitStartDate'
                    type='date'
                />

                <Input
                    label='Purchase Limit'
                    name='purchaseLimit'
                    type='number'
                    min={0}
                />

                <ImageIndex />

                <ColorInput />

                <LocationSelectors />

                <TerminalIDsSelector />
            </>
        )
    }

    const toNum = (v) => (v === '' || v === undefined) ? null : v;
    const nullableNumbers = ['price', 'signupPrice', 'monthlyPrice', 'value', 'logoAlpha', 'logoColor', 'logoImageIndex', 'groupIndex', 'awardId', 'purchaseLimit', 'quantity', 'kioskItemId'];
    const normalizeProduct = (form) => ({
        ...form,
        details: typeof form.details === 'string'
            ? form.details.split('\n').map(s => s.trim()).filter(Boolean)
            : (form.details ?? []),
        ...Object.fromEntries(nullableNumbers.map(k => [k, toNum(form[k])])),
        logo: {
            color: toNum(form.logoColor),
            alpha: toNum(form.logoAlpha),
            imageIndex: toNum(form.logoImageIndex),
        },
        image: form.image ?? [],
        imageExtension: form.imageExtension ?? '',
    });

    const addProduct = (e) => {
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Add new Product`,
            body: (
                <Form
                    name='mobileProductSetting'
                    inputs={{
                        active: true,
                        appProductType: HSOptions.productType[0].value,
                        kioskItemId: '',
                        useKioskItemName: false,
                        name: '',
                        useKioskItemDetails: false,
                        details: '',
                        quantity: '',
                        useKioskItemPrice: false,
                        price: '',
                        signupPrice: '',
                        monthlyPrice: '',
                        value: '',
                        fulfillable: false,
                        PAYG: false,
                        taxable: false,
                        locations: [],
                        terminalIds: [],
                        logoColor: '',
                        logoAlpha: '',
                        logoImageIndex: 0,
                        groupIndex: '',
                        awardId: '',
                        purchaseLimit: '',
                        purchaseLimitStartDate: '',
                        terms: '',
                    }}
                    onSubmit={e => HamQuest.post(QuestRoute.products.all, normalizeProduct(e))
                        .then(() => {
                            refreshProducts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Add Product'
                >
                    {({ values }) => <MobileProducts values={values} />}
                </Form>
            )
        })
    }

    const editProduct = (e) => {
        // console.log(e)
        const id = generateId();
        ModalFactory.show({
            id,
            title: `Edit ${e.name}`,
            body: (
                <Form
                    name='mobileProductSetting'
                    inputs={{
                        ...e,
                        details: Array.isArray(e.details) ? e.details.join('\n') : (e.details ?? ''),
                    }}
                    onSubmit={e => HamQuest.put(QuestRoute.products.edit(e.appProductId), normalizeProduct(e))
                        .then(() => {
                            refreshProducts();
                            ModalFactory.close(id);
                        })
                    }
                    text='Update'
                >
                    {({ values }) => <MobileProducts values={values} />}
                </Form>
            )
        })
    }

    return (
        <Table
            config={{
                controls: [<Button roles={userRoles.editProducts} onClick={addProduct}>Add Product</Button>],
                data: products,
                headers: [
                    {
                        label: 'Product Name',
                        propKey: 'name',
                    }, {
                        label: 'Product Type',
                        propKey: 'productType',
                        options: true,
                    }, {
                        label: 'Kiosk Item',
                        propKey: 'name',
                        options: kioskItems.map(e => e.name),
                    }, {
                        label: 'Quantity',
                        propKey: 'quantity',
                        type: 'number'
                    }, {
                        label: 'Price',
                        propKey: 'price',
                        type: 'number'
                    }, {
                        label: 'Active',
                        propKey: 'active',
                        options: true
                    },
                ],
                quickSort: [
                    {
                        label: 'Active Products',
                        propKey: 'active',
                        query: 'true',
                        color: '#21D59B',
                        defaultSearch: true
                    },
                ],
                renderFormat: (e) => (
                    <ManagementRow
                        key={generateId()}
                        controls={[
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => editProduct(e)}>Edit</Button>,
                            <Button roles={userRoles.editProducts} key={generateId()} onClick={() => deleteProduct(e)}>Delete</Button>
                        ]}
                    >
                        <td>{e.name}</td>
                        <td>{translate(e.productType)}</td>
                        <td>{kioskItems.find(f => f.id === e.kioskItemId)?.name || e.kioskItemId}</td>
                        <td>{e.quantity}</td>
                        <td>{formatCurrency(e.price)}</td>
                        <Ctd><CheckMark checked={e.active} /></Ctd>
                    </ManagementRow>
                )
            }}
        />
    );
}

export default Products;